import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProduct, fetchUnit } from "../../redux/userSetting";
import AddProduction from "../../components/transactions/production/AddProduction";
import { getproductionbybranch } from "../../services/StockApiServices";
import { getProductBOQlist } from "../../services/MasterApiServices";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, money } from "../../components/report/reportFormat";

const Production = () => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("Add");
  const [productionData, setproductionData] = useState([]);
  const [ProductBOQData, setProductBOQData] = useState([]);
  const [updateData, setUpdateData] = useState();
  const { branch } = useSelector((state) => state.branchlist);
  const { unitList, productList } = useSelector((state) => state.usersetting);
  const { menuItems } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Production")
    );
  }, []);
  useEffect(() => {
    handleProductionList();
    handleProductBoqList();
    dispatch(fetchUnit());
    dispatch(fetchProduct(branch));
  }, [branch]);

  const handleProductionList = async () => {
    try {
      const response = await getproductionbybranch(branch);
      setproductionData(response.data);
    } catch (error) {
      console.log();
    }
  };

  const handleEditClick = async (id, action) => {
    setModalMode(action); // Set the modal mode to 'edit'
    setModalOpen(true); // Open the modal
    const data = productionData?.find((e) => id == e.guid);
    setUpdateData(data);
  };

  const handleProductBoqList = async () => {
    try {
      const response = await getProductBOQlist(branch);

      if (Array.isArray(response)) {
        setProductBOQData(response);
      } else {
        setProductBOQData([]);
      }
    } catch (error) {
      console.log(error?.response?.data?.Message);
      setProductBOQData([]);
    }
  };

  /* ---------------- list ---------------- */
  const unitMap = useMemo(
    () => Object.fromEntries((unitList || []).map((u) => [u.guid, u.name])),
    [unitList]
  );
  const productMap = useMemo(
    () => Object.fromEntries((productList || []).map((p) => [p.guid, p])),
    [productList]
  );

  // one flat record per production entry (one finished product each)
  const rows = useMemo(
    () =>
      (Array.isArray(productionData) ? productionData : []).map((entry) => {
        const lines = entry?.invProductionDetails || [];
        return {
          guid: entry.guid,
          raw: entry,
          date: dateOf(entry?.date),
          billNo: entry?.entryno || "",
          // only "WP" entries could be edited from the old list
          isWP: entry?.entryno?.slice(0, 2) === "WP",
          product: entry?.product?.name || "",
          productGuid: entry?.product?.guid,
          productCode: entry?.product?.code || "",
          qty: entry?.quanitity,
          unit: unitMap[entry?.unit?.guid] || entry?.unit?.name || "",
          items: lines.length,
          lines,
        };
      }),
    [productionData, unitMap]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Entry No.", type: "doc" },
      { k: "product", t: "Product" },
      { k: "qty", t: "Quantity", type: "int" },
      { k: "unit", t: "Unit" },
      { k: "items", t: "Materials", type: "int", off: true, noSum: true },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Production" }], []);

  const filters = useMemo(
    () => [
      {
        key: "products",
        label: "Product",
        items: ProductBOQData.map((p) => ({ v: p.guid, t: p.name || "—" })),
        match: (r, v) => v.includes(r.productGuid),
      },
    ],
    [ProductBOQData]
  );

  const lineProduct = (l) => {
    const p = productMap[l?.product?.guid] || {};
    return { name: l?.product?.name || p.name, code: l?.product?.code || p.code };
  };

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.product) ||
      hit(r.productCode) ||
      hit(r.unit) ||
      hit(r.date) ||
      r.lines.some((l) => {
        const p = lineProduct(l);
        return hit(p.name) || hit(p.code);
      })
    );
  };

  const drawerContent = (r) => ({
    title: r.product || "Production",
    subtitle: [r.billNo, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.product || r.billNo,
    meta: [
      { k: "Product", v: r.product },
      { k: "Quantity", v: [intg(r.qty), r.unit].filter(Boolean).join(" ") },
      { k: "Date", v: dmy(r.date) },
      { k: "Materials", v: intg(r.items) },
    ],
    lines: {
      title: "Materials used",
      columns: [
        { t: "Item" },
        { t: "Qty", align: "right" },
        { t: "Cost", align: "right" },
      ],
      rows: r.lines.map((l) => {
        const p = lineProduct(l);
        return [
          <>
            <div className="axr-nm">{p.name || "Unknown Item"}</div>
            <div className="axr-cd">
              {[p.code, l?.unit?.name || unitMap[l?.unit?.guid]].filter(Boolean).join(" · ")}
            </div>
          </>,
          intg(l?.productQty),
          money(l?.cost),
        ];
      }),
    },
  });

  // the old list showed the actions column only to users with edit or delete
  // rights: "Edit" on WP entries (edit right), "View" (read-only form) on all
  const canOpen = permit?.edit || permit?.deletion;

  return (
    <TransactionScreen
      title="Production"
      unit="entries"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, product, material…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "billNo:-1", t: "Entry no (high–low)" },
        { v: "billNo:1", t: "Entry no (low–high)" },
        { v: "product:1", t: "Product A–Z" },
      ]}
      exportName="Production"
      onRefresh={handleProductionList}
      // the old "Add New" button was not permission-gated
      canAdd
      newLabel="New production"
      onNew={() => {
        setModalMode("Add");
        setUpdateData();
        setModalOpen(true);
      }}
      canEdit={canOpen}
      onEdit={(r) => handleEditClick(r.guid, permit?.edit && r.isWP ? "Edit" : "View")}
      editLabel="Open production"
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <AddProduction
          productionData={productionData}
          ProductBOQData={ProductBOQData}
          mode={modalMode}
          updateData={updateData}
          handleClose={() => setModalOpen(false)}
          onsuccess={handleProductionList}
        />
      )}
    </TransactionScreen>
  );
};

export default Production;
